import { useEffect, useState } from 'react'
import { GitCompareArrows, X } from 'lucide-react'
import { Link } from '../router.js'
import { Trail } from '../navigation/Trail.js'
import { SmartBack } from '../navigation/SmartBack.js'
import { Select } from '../admin/primitives.js'
import { remplacerRecherche } from '../finance/adresse-ecran.js'
import { useLecture } from './associations-api.js'
import { BarreMix, COULEURS_SERIES, MiniGraphe } from './Courbes.js'
import { Chargement, Erreur, PastilleKind } from './AssociationsCommun.js'
import {
  CHEMIN_COMPARER, CHEMIN_LISTE, COMPARER_MAX, COMPARER_MIN, cheminFiche, idsAComparer, joursAvant, nombre, nuitLongue,
  pourcent, texteTendance,
  type Comparaison, type ReponseListe,
} from './associations-model.js'

/**
 * EXPLOITATION › ASSOCIATIONS › COMPARER (`/exploitation/associations/comparer?ids=…`, 20/10/2026).
 *
 * De 2 à 6 associations côte à côte : un tableau (la vue de référence,
 * lisible sans couleur), puis de petites courbes. Les associations n'ont pas
 * leurs soirées aux mêmes dates : les courbes alignent leurs DERNIÈRES
 * soirées (« il y a 3 soirées… la dernière »), la date exacte au survol.
 * Chaque association garde sa couleur, dans l'ordre de la sélection.
 *
 * La sélection est dans l'adresse : un lien envoyé rouvre la même comparaison.
 */

/** Combien de soirées récentes les courbes alignent. */
const DERNIERES = 8

export function ComparerAssociationsPage() {
  const [ids, setIds] = useState<string[]>(() => idsAComparer(new URLSearchParams(window.location.search)))
  useEffect(() => {
    const p = new URLSearchParams()
    if (ids.length > 0) p.set('ids', ids.join(','))
    remplacerRecherche(CHEMIN_COMPARER, p)
  }, [ids])
  const assez = ids.length >= COMPARER_MIN
  const { etat, relire } = useLecture<{ associations: Comparaison[] }>(assez ? `/api/v1/associations/comparer?ids=${ids.join(',')}` : null)
  const { etat: liste } = useLecture<ReponseListe>('/api/v1/associations?statut=toutes')
  const choix = liste.phase === 'ok' ? liste.data.associations.filter((a) => (a.status === 'ACTIVE' || a.status === 'ARCHIVED') && !ids.includes(a.id)) : []
  const noms = new Map(liste.phase === 'ok' ? liste.data.associations.map((a) => [a.id, a.name] as const) : [])

  return (
    <main className="lc-page lc-page--wide lc-stagger asso-page">
      <Trail leaf="Comparer" />
      <SmartBack fallback={CHEMIN_LISTE} />
      <header className="asso-hero">
        <div>
          <h1 className="lc-title"><GitCompareArrows size={26} aria-hidden="true" /> Comparer des associations</h1>
          <p className="lc-subtitle">Billets vendus, entrées scannées, absents, formules et avance des ventes, côte à côte.</p>
        </div>
      </header>

      <section className="asso-selection" aria-label="Associations comparées">
        <ul className="asso-puces">
          {ids.map((id, i) => (
            <li key={id} className="asso-puce asso-puce--serie">
              <i style={{ background: COULEURS_SERIES[i % COULEURS_SERIES.length] }} aria-hidden="true" />
              {noms.get(id) ?? '…'}
              <button type="button" className="asso-puce__retirer" aria-label={`Retirer ${noms.get(id) ?? 'cette association'} de la comparaison`}
                onClick={() => setIds(ids.filter((x) => x !== id))}><X size={12} aria-hidden="true" /></button>
            </li>
          ))}
        </ul>
        {ids.length < COMPARER_MAX ? (
          <Select compact value="" ariaLabel="Ajouter une association à la comparaison" placeholder="Ajouter une association"
            options={choix.map((a) => ({ value: a.id, label: a.name }))} onChange={(v) => { if (v) setIds([...ids, v]) }} />
        ) : <span className="lc-hint">{COMPARER_MAX} au plus.</span>}
      </section>

      {!assez ? (
        <div className="asso-vide">
          <p className="asso-vide__titre">Choisissez au moins {COMPARER_MIN} associations</p>
          <p className="lc-hint">Ajoutez-les ci-dessus, ou cochez-les dans <Link href={CHEMIN_LISTE}>la liste</Link>.</p>
        </div>
      ) : etat.phase === 'chargement' ? <Chargement libelle="Comparaison" />
        : etat.phase === 'erreur' ? <Erreur message={etat.message} onReessayer={relire} />
          : <Comparaisons c={etat.data.associations} />}
    </main>
  )
}

function Comparaisons({ c }: { c: Comparaison[] }) {
  const couleur = (i: number) => COULEURS_SERIES[i % COULEURS_SERIES.length]!
  // Les DERNIÈRES soirées de chacune, alignées à droite : la dernière tombe sur « dernière ».
  const n = Math.min(DERNIERES, Math.max(...c.map((x) => x.serie.length), 0))
  const colonnes = Array.from({ length: n }, (_, i) => (i === n - 1 ? 'dernière' : `−${n - 1 - i}`))
  const aligne = <T,>(serie: T[]): Array<T | null> => {
    const fin = serie.slice(-n)
    return [...Array<null>(n - fin.length).fill(null), ...fin]
  }
  const series = (f: (s: Comparaison['serie'][number]) => number | null) => c.map((x, i) => ({
    nom: x.association.name, couleur: couleur(i), valeurs: aligne(x.serie).map((s) => (s ? f(s) : null)),
  }))
  const lignes: Array<[string, (x: Comparaison) => React.ReactNode]> = [
    ['Type', (x) => <PastilleKind kind={x.association.kind} />],
    ['Soirées', (x) => nombre(x.indicateurs.nuits)],
    ['Billets vendus', (x) => nombre(x.indicateurs.vendus_total)],
    ['Par soirée', (x) => nombre(x.indicateurs.moyenne_vendus)],
    // « Entrées » : des scans, sauf sur une nuit marquée à la main (entrées saisies) ; inconnues → « — ».
    ['Entrées', (x) => `${nombre(x.indicateurs.entrees_total)}${x.indicateurs.nuits_entrees_saisies > 0 ? ' *' : ''}`],
    ['Absents', (x) => pourcent(x.indicateurs.no_show_pct)],
    ['Tendance', (x) => texteTendance(x.indicateurs.tendance)],
    ['Dernière soirée', (x) => (x.indicateurs.derniere_nuit ? nuitLongue(x.indicateurs.derniere_nuit) : '—')],
    ['Prochaine soirée', (x) => (x.indicateurs.prochaine_nuit ? nuitLongue(x.indicateurs.prochaine_nuit) : '—')],
  ]
  return (
    <>
      <section className="lc-panel gd-panel asso-bloc" aria-label="Tableau comparatif">
        <div className="lc-panel__body asso-defile">
          <table className="lc-table asso-table asso-table--comparer">
            <thead>
              <tr>
                <th scope="col"><span className="asso-masque">Indicateur</span></th>
                {c.map((x, i) => (
                  <th key={x.association.id} scope="col">
                    <i className="asso-pastille-serie" style={{ background: couleur(i) }} aria-hidden="true" />
                    <Link href={cheminFiche(x.association.id)}>{x.association.name}</Link>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lignes.map(([libelle, f]) => (
                <tr key={libelle}><th scope="row">{libelle}</th>{c.map((x) => <td key={x.association.id}>{f(x)}</td>)}</tr>
              ))}
            </tbody>
          </table>
          {c.some((x) => x.indicateurs.nuits_entrees_saisies > 0) ? (
            <p className="lc-hint">« * » : une partie des entrées est saisie (nuit marquée à la main, ou celles de la Comptabilité), pas scannée.</p>
          ) : null}
        </div>
      </section>

      {n > 0 ? (
        <section className="lc-panel gd-panel asso-bloc" aria-labelledby="cmp-courbes">
          <header className="lc-panel__head"><h2 className="lc-section-title" id="cmp-courbes">Leurs {n > 1 ? `${n} dernières soirées` : 'dernière soirée'}</h2></header>
          <div className="lc-panel__body asso-graphes">
            <figure className="asso-figure">
              <figcaption>Billets vendus</figcaption>
              <MiniGraphe kind="ligne" colonnes={colonnes} ariaLabel="Billets vendus, dernières soirées" series={series((s) => s.vendus)} />
            </figure>
            <figure className="asso-figure">
              <figcaption>Entrées scannées</figcaption>
              <MiniGraphe kind="ligne" colonnes={colonnes} ariaLabel="Entrées, dernières soirées" series={series((s) => s.entrees)} />
            </figure>
            <figure className="asso-figure">
              <figcaption>Absents, en %</figcaption>
              <MiniGraphe kind="ligne" pourcentage colonnes={colonnes} ariaLabel="Absents, dernières soirées" format={(v) => pourcent(v)} series={series((s) => s.no_show_pct)} />
            </figure>
            <figure className="asso-figure">
              <figcaption>Première vente, en jours avant la soirée</figcaption>
              <MiniGraphe kind="ligne" colonnes={colonnes} ariaLabel="Avance des ventes, dernières soirées" format={(v) => joursAvant(v)} series={series((s) => s.ventes_avant_jours)} />
            </figure>
          </div>
          <p className="lc-hint asso-bloc__note">« −3 » : trois soirées avant la dernière de chaque association. Un trou : chiffre inconnu (aucun scan relevé), jamais 0.</p>
        </section>
      ) : null}

      <section className="lc-panel gd-panel asso-bloc" aria-labelledby="cmp-mix">
        <header className="lc-panel__head"><h2 className="lc-section-title" id="cmp-mix">Formules vendues</h2></header>
        <div className="lc-panel__body asso-mix-grille">
          {c.map((x) => <BarreMix key={x.association.id} titre={x.association.name} mix={x.indicateurs.mix} />)}
        </div>
      </section>
    </>
  )
}
